import React, { useState, useMemo, useEffect } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import {
  ArrowLeft,
  Calendar,
  Download,
  Share2,
  ChevronDown,
  TrendingUp,
  Search,
  ChevronRight,
  FileText
} from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount || 0);
};

const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getFullYear()}`;
};

const toInputDateFormat = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const SalesReportView = ({ onBack, bills = [] }) => {
  const { t } = useLang();
  const [selectedRange, setSelectedRange] = useState('this_month');
  const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [toDate, setToDate] = useState(new Date());
  const [businessProfile, setBusinessProfile] = useState(null);

  useEffect(() => {
    const fetchBiz = async () => {
      try {
        const { getBusinessProfile } = await import('../../services/dashboardApi');
        const res = await getBusinessProfile();
        if (res?.success) setBusinessProfile(res.data);
      } catch (err) {
        console.error('Error fetching biz profile:', err);
      }
    };
    fetchBiz();
  }, []);

  const handleDownloadPdf = () => {
    window.print();
  };

  const handleShare = async () => {
    const bizName = localStorage.getItem("business_name") || "Moxpal Khata";
    const reportText = `*Sales Report - ${bizName}*\n` +
      `---------------------------\n` +
      `*Period:* ${formatDate(fromDate)} to ${formatDate(toDate)}\n` +
      `*Transactions:* ${summary.count}\n` +
      `*Net Sales:* ${formatCurrency(summary.totalSales)}\n` +
      `*Unpaid Balance:* ${formatCurrency(summary.unpaidBalance)}\n` +
      `---------------------------\n` +
      `Generated via Moxpal Khata`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Sales Report',
          text: reportText,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing report:', error);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(reportText);
        alert('Sales report summary copied to clipboard! You can now paste and share it.\n\nरिपोर्ट का विवरण क्लिपबोर्ड पर कॉपी हो गया है!');
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleFromDateChange = (e) => {
    const val = e.target.value;
    if (val) {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        setFromDate(d);
        setSelectedRange('custom');
      }
    }
  };

  const handleToDateChange = (e) => {
    const val = e.target.value;
    if (val) {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        setToDate(d);
        setSelectedRange('custom');
      }
    }
  };

  const applyDateRange = (type) => {
    if (type === 'custom') {
      setSelectedRange('custom');
      return;
    }
    const now = new Date();
    let from, to;
    switch (type) {
      case 'today':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'last_week':
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        to = now;
        break;
      case 'this_month':
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        break;
      case 'last_month':
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      case 'all':
        from = new Date(2020, 0, 1);
        to = now;
        break;
      default:
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    }
    setFromDate(from);
    setToDate(to);
    setSelectedRange(type);
  };

  const dateRanges = [
    { value: 'custom', label: t(LangKeys.date_range) },
    { value: 'today', label: t(LangKeys.single_day) },
    { value: 'this_month', label: t(LangKeys.this_month) },
    { value: 'last_week', label: t(LangKeys.last_week) },
    { value: 'last_month', label: t(LangKeys.last_month) },
    { value: 'all', label: t(LangKeys.all) },
  ];

  const summary = useMemo(() => {
    const fromStart = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
    const toEnd = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate(), 23, 59, 59);

    const filtered = bills.filter(b => {
      const bDate = new Date(b.date);
      return b.type === 'sale' && bDate >= fromStart && bDate <= toEnd;
    });

    const totalSales = filtered.reduce((s, b) => s + (b.finalBillAmount || 0), 0);
    const unpaidBalance = filtered
      .filter(b => {
        const pType = b.paymentType?.toLowerCase();
        return pType !== 'cash' && pType !== 'online';
      })
      .reduce((s, b) => s + (b.finalBillAmount || 0), 0);

    return { totalSales, unpaidBalance, count: filtered.length, filtered };
  }, [bills, fromDate, toDate]);

  return (
    <div className="print-main-wrapper" style={styles.container}>
      <div className="no-print" style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={onBack} className="no-print" style={styles.backBtn}><ArrowLeft size={20} /></button>
          <h3 style={styles.title}>{t(LangKeys.sales_report)}</h3>
        </div>
      </div>

      <div className="no-print" style={styles.scrollContent}>
        {/* DATE FILTERS */}
        <div style={styles.filterCard}>
          <div style={styles.dateRow}>
            <div style={styles.dateChip}>
              <input
                type="date"
                value={toInputDateFormat(fromDate)}
                onChange={handleFromDateChange}
                style={styles.dateInput}
              />
            </div>
            <span style={styles.toLabel}>{t(LangKeys.to)}</span>
            <div style={styles.dateChip}>
              <input
                type="date"
                value={toInputDateFormat(toDate)}
                onChange={handleToDateChange}
                style={styles.dateInput}
              />
            </div>
          </div>
          <div className="no-print" style={styles.dropdownBox}>
            <select
              style={styles.select}
              value={selectedRange}
              onChange={(e) => applyDateRange(e.target.value)}
            >
              {dateRanges.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <ChevronDown size={18} color="#64748B" />
          </div>
        </div>

        {/* SUMMARY CARD */}
        <div style={styles.summaryCard}>
          <div style={styles.sumItem}>
            <span style={styles.sumLabel}>{t(LangKeys.transactions)}</span>
            <span style={styles.sumVal}>{summary.count}</span>
          </div>
          <div style={styles.sumItem}>
            <span style={styles.sumLabel}>{t(LangKeys.net_sale)}</span>
            <span style={{ ...styles.sumVal, color: '#10B981' }}>{formatCurrency(summary.totalSales)}</span>
          </div>
          <div style={styles.sumItem}>
            <span style={styles.sumLabel}>{t(LangKeys.unpaid_balance)}</span>
            <span style={{ ...styles.sumVal, color: '#EF4444' }}>{formatCurrency(summary.unpaidBalance)}</span>
          </div>
        </div>

        {/* LIST */}
        <div style={styles.listSection}>
          {summary.filtered.length === 0 ? (
            <div style={styles.emptyState}>
              <Search size={48} color="#E2E8F0" />
              <p>{t(LangKeys.no_transactions_found_period)}</p>
            </div>
          ) : (
            summary.filtered.map((bill, i) => {
              const isPaid = bill.paymentType?.toLowerCase() === 'cash' || bill.paymentType?.toLowerCase() === 'online';
              const color = isPaid ? '#16A34A' : '#DC2626';

              return (
                <div key={i} style={styles.txnTile}>
                  <div style={{ ...styles.iconCircle, background: `${color}15` }}>
                    <ArrowLeft
                      size={20}
                      color={color}
                      style={{ transform: 'rotate(-90deg)' }}
                    />
                  </div>
                  <div style={styles.txnInfo}>
                    <span style={styles.txnTitle}>{bill.customer?.customerName}</span>
                    <span style={styles.txnDate}>{new Date(bill.date).toLocaleString()}</span>
                  </div>
                  <div style={styles.txnRight}>
                    <span style={{ ...styles.txnAmount, color }}>{formatCurrency(bill.finalBillAmount)}</span>
                    <ChevronRight size={16} color="#CBD5E1" />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="no-print" style={styles.bottomBar}>
        <button onClick={handleDownloadPdf} style={styles.downloadBtn}>
          <Download size={18} />
          <span>{t(LangKeys.download_pdf).toUpperCase()}</span>
        </button>
        <button onClick={handleShare} style={styles.shareBtn}>
          <Share2 size={18} />
          <span>{t(LangKeys.share).toUpperCase()}</span>
        </button>
      </div>

      {/* Printable Report Sheet */}
      <div className="print-only" id="sales-report-pdf-template" style={{
        padding: '10mm 15mm',
        boxSizing: 'border-box',
        background: 'white',
        fontFamily: "'Outfit', 'Hind', sans-serif",
        color: '#1e293b',
        textAlign: 'left',
        width: '100%'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center' }}>
            {businessProfile?.logo_url && <img src={businessProfile.logo_url} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} alt="" />}
          </div>
          <div style={{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <img src="https://khata.moxpal.com/assets/logo.png" style={{ height: '22px', marginBottom: '6px' }} alt="Moxpal" />
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>{businessProfile?.business_name || t(LangKeys.my_business)}</h2>
            {businessProfile?.owner_name && <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px' }}>{t(LangKeys.name)}: {businessProfile.owner_name}</div>}
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{t(LangKeys.mobile_no)}: {businessProfile?.mobile || ''}</div>
          </div>
          <div style={{ width: '60px', height: '60px', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
            <img src="https://khata.moxpal.com/assets/ganesh-ji.png" style={{ width: '40px', height: '40px', objectFit: 'contain' }} alt="Ganesh" />
          </div>
        </div>

        <div style={{ background: '#1e40af', color: 'white', padding: '10px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px', margin: '20px 0', borderRadius: '4px', textTransform: 'uppercase' }}>
          {t(LangKeys.sales_report).toUpperCase()}
        </div>

        <div style={{ textAlign: 'center', fontSize: '12px', color: '#475569', marginBottom: '20px', fontWeight: '600' }}>
          ({formatDate(fromDate)} - {formatDate(toDate)})
        </div>

        <div style={{ display: 'flex', gap: '15px', marginBottom: '30px' }}>
          <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
            <span style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px', display: 'block' }}>{t(LangKeys.transactions)}</span>
            <span style={{ fontSize: '16px', fontWeight: '800', color: '#1e2937' }}>{summary.count}</span>
          </div>
          <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
            <span style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px', display: 'block' }}>{t(LangKeys.net_sale)}</span>
            <span style={{ fontSize: '16px', fontWeight: '800', color: '#10b981' }}>{formatCurrency(summary.totalSales)}</span>
          </div>
          <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
            <span style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px', display: 'block' }}>{t(LangKeys.unpaid_balance)}</span>
            <span style={{ fontSize: '16px', fontWeight: '800', color: '#ef4444' }}>{formatCurrency(summary.unpaidBalance)}</span>
          </div>
        </div>

        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#0f172a', marginBottom: '12px', borderLeft: '4px solid #1e40af', paddingLeft: '10px' }}>
          All Transactions
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #cbd5e1', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'left', width: '22%' }}>DATE</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'left', width: '43%' }}>PARTY/DETAILS</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'center', width: '15%' }}>STATUS</th>
              <th style={{ border: '1px solid #cbd5e1', padding: '12px', background: '#f8fafc', fontSize: '11px', fontWeight: '800', color: '#475569', textAlign: 'right', width: '20%' }}>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {summary.filtered.length > 0 ? summary.filtered.map((bill, i) => {
              const isPaid = bill.paymentType?.toLowerCase() === 'cash' || bill.paymentType?.toLowerCase() === 'online';
              const status = isPaid ? t(LangKeys.paid || 'Paid') : t(LangKeys.unpaid || 'Unpaid');
              const statusColor = isPaid ? '#16a34a' : '#ef4444';
              return (
                <tr key={i}>
                  <td style={{ border: '1px solid #cbd5e1', padding: '12px', fontSize: '12px', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formatDate(bill.date)}
                  </td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '12px', fontSize: '12px', color: '#334155', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {bill.customer?.customerName || 'Unknown'}
                  </td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '12px', fontSize: '12px', textAlign: 'center', fontWeight: 'bold', color: statusColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {status}
                  </td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '12px', fontSize: '12px', color: '#334155', textAlign: 'right', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formatCurrency(bill.finalBillAmount)}
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan="4" style={{ border: '1px solid #cbd5e1', padding: '40px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
                  No transactions found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div style={{ marginTop: '50px', borderTop: '1px solid #F1F5F9', paddingTop: '20px', textAlign: 'center', fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>
          {t(LangKeys.generated_by)} | {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: { display: 'flex', flexDirection: 'column', height: '100%', background: '#F3F4F6' },
  header: { padding: '20px 24px', background: '#1D4ED8', color: 'white', display: 'flex', alignItems: 'center' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  backBtn: { width: '36px', height: '36px', borderRadius: '10px', border: 'none', background: 'rgba(255,255,255,0.1)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  title: { fontSize: '18px', fontWeight: '800', margin: 0 },
  scrollContent: { flex: 1, padding: '14px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '14px' },
  filterCard: { background: 'white', padding: '16px', borderRadius: '18px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '12px' },
  dateRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  dateChip: { flex: 1, height: '44px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '600' },
  toLabel: { fontSize: '13px', fontWeight: '700', color: '#6B7280' },
  dropdownBox: { background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '12px', height: '44px', padding: '0 12px', display: 'flex', alignItems: 'center' },
  select: { flex: 1, border: 'none', background: 'transparent', outline: 'none', fontWeight: '700', fontSize: '14px', color: '#1F2937' },
  summaryCard: { background: 'white', padding: '20px', borderRadius: '18px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  sumItem: { display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center', flex: 1 },
  sumLabel: { fontSize: '12px', color: '#6B7280', fontWeight: '700', textAlign: 'center' },
  sumVal: { fontSize: '16px', fontWeight: '900', color: '#1E2937' },
  listSection: { display: 'flex', flexDirection: 'column', gap: '10px' },
  txnTile: { background: 'white', padding: '14px 16px', borderRadius: '14px', display: 'flex', alignItems: 'center', gap: '14px', boxShadow: '0 2px 6px rgba(0,0,0,0.02)' },
  iconCircle: { width: '44px', height: '44px', borderRadius: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  txnInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' },
  txnTitle: { fontSize: '15px', fontWeight: '700', color: '#374151' },
  txnDate: { fontSize: '12px', color: '#6B7280' },
  txnRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  txnAmount: { fontSize: '15px', fontWeight: '800' },
  emptyState: { padding: '40px', textAlign: 'center', color: '#9CA3AF', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' },
  bottomBar: { padding: '16px 20px', background: 'white', borderTop: '1px solid #E5E7EB', display: 'flex', gap: '12px' },
  downloadBtn: { flex: 1, height: '48px', borderRadius: '12px', border: 'none', background: '#1D4ED8', color: 'white', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  shareBtn: { flex: 1, height: '48px', borderRadius: '12px', border: 'none', background: '#16A34A', color: 'white', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  dateInput: {
    border: 'none',
    background: 'transparent',
    fontWeight: '600',
    fontSize: '14px',
    color: '#1F2937',
    fontFamily: 'inherit',
    outline: 'none',
    width: '100%',
    height: '100%',
    textAlign: 'center',
    cursor: 'pointer',
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
};

export default SalesReportView;
